// src/pages/Appointments/Admin/VisitTracking/ActivityTracker.jsx
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  FiEye,
  FiShoppingBag,
  FiX,
  FiPlus,
  FiMessageSquare,
  FiSave,
} from "react-icons/fi";
import {
  trackProductViewedThunk,
  trackProductTriedOnThunk,
  addInteractionThunk,
  updateVisitNotesThunk,
} from "../../../../store/thunks/appointmentThunks";
import Button from "../../../../components/common/Button";
import toast from "react-hot-toast";

const ActivityTracker = ({ appointment, onClose, onUpdate }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("products");
  const [loading, setLoading] = useState(false);

  // Product tracking state
  const [productData, setProductData] = useState({
    productCode: "",
    viewDuration: "",
    customerFeedback: "",
    adminNotes: "",
  });

  // Interaction state
  const [interactionData, setInteractionData] = useState({
    interactionType: "PRODUCT_SHOWING",
    notes: "",
  });

  // Visit notes state
  const [visitNotes, setVisitNotes] = useState({
    visitNotes: appointment.visit?.adminNotes?.visitNotes || "",
    customerBehavior: appointment.visit?.adminNotes?.customerBehavior || "",
    productPreferences: appointment.visit?.adminNotes?.productPreferences || "",
  });

  // ========================================
  // HANDLERS
  // ========================================

  const handleTrackViewed = async () => {
    if (!productData.productCode) {
      toast.error("Product code is required");
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        trackProductViewedThunk({
          appointmentId: appointment._id,
          productData: {
            productCode: productData.productCode,
            viewDuration: Number(productData.viewDuration) || 0,
          },
        })
      ).unwrap();
      toast.success("Product view tracked");
      setProductData({
        productCode: "",
        viewDuration: "",
        customerFeedback: "",
        adminNotes: "",
      });
      onUpdate();
    } catch (error) {
      toast.error(error || "Failed to track product view");
    } finally {
      setLoading(false);
    }
  };

  const handleTrackTriedOn = async () => {
    if (!productData.productCode) {
      toast.error("Product code is required");
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        trackProductTriedOnThunk({
          appointmentId: appointment._id,
          productData: {
            productCode: productData.productCode,
            customerFeedback: productData.customerFeedback,
            adminNotes: productData.adminNotes,
          },
        })
      ).unwrap();
      toast.success("Product try-on tracked");
      setProductData({
        productCode: "",
        viewDuration: "",
        customerFeedback: "",
        adminNotes: "",
      });
      onUpdate();
    } catch (error) {
      toast.error(error || "Failed to track product try-on");
    } finally {
      setLoading(false);
    }
  };

  const handleAddInteraction = async () => {
    if (!interactionData.notes) {
      toast.error("Interaction notes are required");
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        addInteractionThunk({
          appointmentId: appointment._id,
          interactionData,
        })
      ).unwrap();
      toast.success("Interaction recorded");
      setInteractionData({ interactionType: "PRODUCT_SHOWING", notes: "" });
      onUpdate();
    } catch (error) {
      toast.error(error || "Failed to record interaction");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    setLoading(true);
    try {
      await dispatch(
        updateVisitNotesThunk({
          appointmentId: appointment._id,
          notesData: visitNotes,
        })
      ).unwrap();
      toast.success("Visit notes saved");
      onUpdate();
    } catch (error) {
      toast.error(error || "Failed to save notes");
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // TABS
  // ========================================

  const tabs = [
    { key: "products", label: "Products", icon: FiShoppingBag },
    { key: "interactions", label: "Interactions", icon: FiMessageSquare },
    { key: "notes", label: "Notes", icon: FiSave },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-text">
                Track Visit Activities
              </h3>
              <p className="text-sm text-neutral-dark">
                Record customer interactions and product views
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-neutral-light rounded-full transition-colors"
            >
              <FiX className="text-xl text-neutral-dark" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4 border-b">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? "border-primary text-primary font-medium"
                      : "border-transparent text-neutral-dark hover:text-primary"
                  }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Products Tab */}
          {activeTab === "products" && (
            <div className="space-y-6">
              {/* Track Viewed */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-text mb-3 flex items-center gap-2">
                  <FiEye className="text-blue-600" />
                  Track Product Viewed
                </h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Product Code"
                    value={productData.productCode}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        productCode: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="number"
                    placeholder="View Duration (seconds) - Optional"
                    value={productData.viewDuration}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        viewDuration: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Button
                    onClick={handleTrackViewed}
                    loading={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <FiPlus className="mr-2" />
                    Track Viewed
                  </Button>
                </div>
              </div>

              {/* Track Tried On */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-text mb-3 flex items-center gap-2">
                  <FiShoppingBag className="text-purple-600" />
                  Track Product Tried On
                </h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Product Code"
                    value={productData.productCode}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        productCode: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <textarea
                    placeholder="Customer Feedback"
                    value={productData.customerFeedback}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        customerFeedback: e.target.value,
                      })
                    }
                    rows="2"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <textarea
                    placeholder="Admin Notes"
                    value={productData.adminNotes}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        adminNotes: e.target.value,
                      })
                    }
                    rows="2"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <Button
                    onClick={handleTrackTriedOn}
                    loading={loading}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    <FiPlus className="mr-2" />
                    Track Tried On
                  </Button>
                </div>
              </div>

              {/* Activity Summary */}
              <div className="bg-neutral-light rounded-lg p-4">
                <h4 className="font-semibold text-text mb-3">
                  Activity Summary
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-neutral-dark">Products Viewed</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {appointment.visit?.activities?.productsViewed?.length ||
                        0}
                    </p>
                  </div>
                  <div>
                    <p className="text-neutral-dark">Products Tried</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {appointment.visit?.activities?.productsTriedOn?.length ||
                        0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Interactions Tab */}
          {activeTab === "interactions" && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interaction Type
                </label>
                <select
                  value={interactionData.interactionType}
                  onChange={(e) =>
                    setInteractionData({
                      ...interactionData,
                      interactionType: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="GREETING">Greeting</option>
                  <option value="CONSULTATION">Consultation</option>
                  <option value="PRODUCT_SHOWING">Product Showing</option>
                  <option value="CLOSING">Closing</option>
                  <option value="FOLLOW_UP">Follow-up Discussion</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interaction Notes
                </label>
                <textarea
                  value={interactionData.notes}
                  onChange={(e) =>
                    setInteractionData({
                      ...interactionData,
                      notes: e.target.value,
                    })
                  }
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Describe the interaction..."
                />
              </div>

              <Button
                onClick={handleAddInteraction}
                loading={loading}
                className="w-full"
              >
                <FiPlus className="mr-2" />
                Record Interaction
              </Button>

              {/* Previous Interactions */}
              {appointment.visit?.adminInteractions?.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-semibold text-text mb-3">
                    Previous Interactions
                  </h4>
                  <div className="space-y-2">
                    {appointment.visit.adminInteractions.map(
                      (interaction, index) => (
                        <div
                          key={index}
                          className="p-3 bg-neutral-light rounded-lg text-sm"
                        >
                          <div className="flex justify-between mb-1">
                            <span className="font-medium">
                              {interaction.interactionType?.replace(/_/g, " ")}
                            </span>
                            <span className="text-neutral-dark">
                              {new Date(
                                interaction.timestamp
                              ).toLocaleTimeString("en-IN")}
                            </span>
                          </div>
                          <p className="text-neutral-dark">
                            {interaction.notes}
                          </p>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === "notes" && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visit Notes
                </label>
                <textarea
                  value={visitNotes.visitNotes}
                  onChange={(e) =>
                    setVisitNotes({ ...visitNotes, visitNotes: e.target.value })
                  }
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="General notes about the visit..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer Behavior
                </label>
                <textarea
                  value={visitNotes.customerBehavior}
                  onChange={(e) =>
                    setVisitNotes({
                      ...visitNotes,
                      customerBehavior: e.target.value,
                    })
                  }
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Observations about customer behavior..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Preferences
                </label>
                <textarea
                  value={visitNotes.productPreferences}
                  onChange={(e) =>
                    setVisitNotes({
                      ...visitNotes,
                      productPreferences: e.target.value,
                    })
                  }
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Note customer's product preferences..."
                />
              </div>

              <Button
                onClick={handleSaveNotes}
                loading={loading}
                className="w-full"
              >
                <FiSave className="mr-2" />
                Save Notes
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-neutral-light">
          <Button variant="outline" onClick={onClose} className="w-full">
            Close Activity Tracker
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ActivityTracker;
